"use client"

import { useEffect, useId, useMemo, useRef, useState } from "react"

import {
  HeatmapCells,
  HeatmapChart,
  HeatmapInteractionBoundary,
  HeatmapInteractionProvider,
  HeatmapLegend,
  HeatmapTooltip,
  HeatmapXAxis,
  HeatmapYAxis,
  type HeatmapLevelStyles,
} from "@/components/bklit/charts/heatmap"
import {
  REGISTRY_ACTIVITY_AS_OF,
  REGISTRY_ACTIVITY_LEVEL_THRESHOLDS,
  registryActivityColumns,
} from "@/lib/marketing/registry-activity"
import chartTheme from "@/components/marketing/articles-activity-heatmap.module.css"
import styles from "./articles.module.css"

// The /articles hub heatmap: new interventional studies posted to
// ClinicalTrials.gov per day, straight from the registry's own daily counts
// (see lib/marketing/registry-activity.ts for provenance). Vendored bklit
// heatmap underneath (components/bklit/charts/heatmap).
//
// Level handling: bklit cells are ALWAYS painted via the levelStyles fill
// scale, whose count->level mapping is GitHub's 1/2/3/4+ contribution
// thresholds; the chart's `colorScale` prop never reaches cell fills
// (heatmap-chart.tsx builds fillScale unconditionally). Daily counts here run
// 0-382, so instead of forking the vendored file we hand the chart counts
// ALREADY bucketed to levels 0-4, which the GitHub mapping passes through
// unchanged (1->1 ... 4+->4). The real count for the hovered day comes from a
// date-keyed lookup so the tooltip reports the actual number, not the bucket.
// Bonus: with cell "counts" equal to levels, the legend's hover sync agrees
// with the fills, so it can stay interactive.

const INK_RAMP = [
  "var(--chart-scale-01)",
  "var(--chart-scale-02)",
  "var(--chart-scale-03)",
  "var(--chart-scale-04)",
  "var(--chart-scale-05)",
] as const

const LEVEL_STYLES = [
  { color: INK_RAMP[0], fillMode: "solid" },
  { color: INK_RAMP[1], fillMode: "solid" },
  { color: INK_RAMP[2], fillMode: "solid" },
  { color: INK_RAMP[3], fillMode: "solid" },
  { color: INK_RAMP[4], fillMode: "solid" },
] as const satisfies HeatmapLevelStyles

function levelForCount(count: number): number {
  if (count <= 0) return 0
  const [t1, t2, t3] = REGISTRY_ACTIVITY_LEVEL_THRESHOLDS
  if (count <= t1) return 1
  if (count <= t2) return 2
  if (count <= t3) return 3
  return 4
}

function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

// CTGov posts on business days; Saturday and Sunday rows are near-permanent
// zeros. Fading them (display rows 5-6 under weekStartDay=1) keeps the true
// story visible without letting two dead rows dominate the grid.
const WEEKEND_ROW_OPACITY = [1, 1, 1, 1, 1, 0.4, 0.4]

export function ArticlesActivityHeatmap() {
  const plotScroll = useRef<HTMLDivElement>(null)
  const hintId = useId()
  const [plotOverflows, setPlotOverflows] = useState(false)
  useEffect(() => {
    const element = plotScroll.current
    if (!element) return
    const measureOverflow = () => setPlotOverflows(element.scrollWidth > element.clientWidth)
    measureOverflow()
    const observer = new ResizeObserver(measureOverflow)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])
  const { leveledColumns, realCounts, dailyBins } = useMemo(() => {
    const columns = registryActivityColumns()
    const counts = new Map<string, number>()
    const leveled = columns.map((col) => ({
      ...col,
      // bklit rotates array positions from Sunday-first to weekStartDay=1.
      // The snapshot is chronological Monday-first, so adapt a copy by native
      // weekday before rotation. Keep source bins intact for the daily table.
      bins: [...col.bins].sort((a, b) => a.bin - b.bin).map((bin) => {
        counts.set(dayKey(bin.date), bin.count)
        return { ...bin, count: levelForCount(bin.count) }
      }),
    }))
    return { leveledColumns: leveled, realCounts: counts, dailyBins: columns.flatMap(column => column.bins) }
  }, [])

  return (
    <figure className={`${chartTheme.chartTheme} ${styles.chartTheme}`}>
      <HeatmapInteractionProvider>
        <HeatmapInteractionBoundary>
          <div ref={plotScroll} className={styles.chartPlotScroll} aria-describedby={plotOverflows ? hintId : undefined} role="region" aria-label="Registry activity over 52 weeks, horizontally scrollable on narrow screens" tabIndex={0}><div className={styles.chartPlot}>
            {/* animate={false}: the enter fade leaves every cell at opacity 0
                until motion's rAF loop runs, so a throttled or scriptless
                renderer (crawlers included) sees an empty grid. This is an
                indexed marketing page; render the data instantly. */}
            <HeatmapChart
              animate={false}
              className="w-full"
              data={leveledColumns}
              gap={3}
              layout="fluid"
              levelStyles={LEVEL_STYLES}
              weekStartDay={1}
            >
              <HeatmapCells rowOpacity={WEEKEND_ROW_OPACITY} />
              <HeatmapXAxis />
              <HeatmapYAxis
                labelFormat="initial"
                rowOpacity={WEEKEND_ROW_OPACITY}
                tickFilter="all"
              />
              <HeatmapTooltip
                instant
                formatLabel={(_, date) => {
                  const real = realCounts.get(dayKey(date)) ?? 0
                  return `${real.toLocaleString("en-GB")} new ${real === 1 ? "study" : "studies"} posted`
                }}
              />
            </HeatmapChart>
          </div></div>
          {plotOverflows && <p id={hintId} className={styles.chartOverflowHint}>Scroll to view the full 52 weeks <span aria-hidden="true">→</span></p>}
            <HeatmapLegend
              lessLabel="Fewer"
              levelStyles={LEVEL_STYLES}
              moreLabel="More"
            />
        </HeatmapInteractionBoundary>
      </HeatmapInteractionProvider>
      <div className="sr-only"><table>
        <caption>Daily new interventional studies posted to ClinicalTrials.gov over the 52 full weeks ending 2 August 2026, from the registry&rsquo;s daily counts as of {REGISTRY_ACTIVITY_AS_OF}.</caption>
        <thead><tr><th scope="col">Date</th><th scope="col">New studies</th></tr></thead>
        <tbody>{dailyBins.map(({ date, count }) => {
          const isoDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
          return <tr key={isoDate}><th scope="row"><time dateTime={isoDate}>{date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</time></th><td>{count.toLocaleString("en-GB")}</td></tr>
        })}</tbody>
      </table></div>
      <figcaption className={styles.chartCaption}>
        New interventional studies posted to ClinicalTrials.gov each day over
        the past 52 weeks, from the registry&rsquo;s own daily counts as of{" "}
        {REGISTRY_ACTIVITY_AS_OF}.
      </figcaption>
    </figure>
  )
}
